import React, { useEffect, useState, useRef } from "react";
import { Table, Spin, message } from "antd";
import Hls from "hls.js";
import { useSearchParams } from "react-router-dom";
import newLogoImage from "../../assets/images/new_logo.jpeg";
import apiPath from "../../constants/apiPath";
import buildImage from "../../assets/images/build.jpeg";
import grindImage from "../../assets/images/grind.jpeg";
import fuseImage from "../../assets/images/fuse.jpeg";
import HLSVideoPlayer from "../../components/HLSVideoPlayer";
import WorkoutTimer from "../../components/WorkoutTimer";

const StreamSection = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("hero");
  const [currentVideo, setCurrentVideo] = useState("");
  const [day, setDay] = useState("FRIDAY");
  const [block, setBlock] = useState("A_BLOCK");
  const blockRef = useRef("A_BLOCK"); // Track current block in ref
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState("#4a2781");
  const [currentRepetition, setCurrentRepetition] = useState(1);
  const [totalRepetitions, setTotalRepetitions] = useState(3);
  const [repetitionMessage, setRepetitionMessage] = useState("");
  const [workoutComplete, setWorkoutComplete] = useState(false);
  const [completionMessage, setCompletionMessage] = useState(
    "You've just completed!",
  );
  const [completionTopMessage, setCompletionTopMessage] =
    useState("Great Job!");
  const [workoutTransitionMessage, setWorkoutTransitionMessage] =
    useState("");
  // Timer states
  const [countdown, setCountdown] = useState(0);
  const [workoutTime, setWorkoutTime] = useState(0);
  const [totalWorkoutTime, setTotalWorkoutTime] = useState(0);
  const [currentBlock, setCurrentBlock] = useState("A_BLOCK");
  const [transitionMessage, setTransitionMessage] = useState("");
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isWorkoutActive, setIsWorkoutActive] = useState(false); // Track if workout is currently running

  // Segment timer states
  const [currentSegment, setCurrentSegment] = useState(0);
  const [totalSegments, setTotalSegments] = useState(0);
  const [segmentTime, setSegmentTime] = useState(0);
  const [isWorkingPhase, setIsWorkingPhase] = useState(true);
  const [segmentInterval, setSegmentInterval] = useState(null);
  const [hlsSupported, setHlsSupported] = useState(true);
  const [videoManifest, setVideoManifest] = useState(null);
  const [videoQuality, setVideoQuality] = useState("auto");
  const [textColor, setTextColor] = useState("#FCDC08");
  const [borderColor, setBorderColor] = useState("#FCDC08");

  const [font1, setFont1] = useState("'Horizon', sans-serif");
  const [font1Color, setFont1Color] = useState("#FCDC08");
  const [font2, setFont2] = useState("'Horizon', sans-serif");
  const [font2Color, setFont2Color] = useState("#FCDC08");
  // Available images mapping
  const [availableImages, setAvailableImages] = useState({
    build: buildImage,
    grind: grindImage,
    fuse: fuseImage,
  });

  // Token state
  const [urlToken, setUrlToken] = useState(null);
  const [media, setMedia] = useState({});
  const [audioPlaying, setAudioPlaying] = useState({
    pre_hype: false,
    warmup: false,
    workout: false,
    preview: false,
  });

  const audioRefs = {
    pre_hype: useRef(null),
    warmup: useRef(null),
    workout: useRef(null),
    preview: useRef(null),
  };

  // Refs to track current state values (avoid stale closures in WebSocket handlers)
  const modeRef = useRef(mode);
  const isPausedRef = useRef(isPaused);
  const isWorkoutActiveRef = useRef(isWorkoutActive);
  const audioPlayingRef = useRef(audioPlaying);
  const currentBlockRef = useRef(currentBlock);
  const countdownRef = useRef(countdown);
  const workoutTimeRef = useRef(workoutTime);

  // Keep refs in sync with state
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    isWorkoutActiveRef.current = isWorkoutActive;
  }, [isWorkoutActive]);

  useEffect(() => {
    audioPlayingRef.current = audioPlaying;
  }, [audioPlaying]);

  useEffect(() => {
    currentBlockRef.current = currentBlock;
  }, [currentBlock]);

  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  useEffect(() => {
    workoutTimeRef.current = workoutTime;
  }, [workoutTime]);

  // Extract token from URL on component mount
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setUrlToken(tokenFromUrl);

      // Optionally store in localStorage if you want to persist it
      localStorage.setItem("accessToken", tokenFromUrl);

      // You can also store it in sessionStorage for just this session
      sessionStorage.setItem("urlToken", tokenFromUrl);
    } else {
    }
  }, [searchParams]);

  const playAudio = (type) => {
    const audio = audioRefs[type]?.current;

    if (!audio) {
      console.log(`[StreamTV] No audio element for ${type}`);
      return Promise.resolve();
    }

    if (!media[`${type}_audio`]) {
      console.log(`[StreamTV] No ${type} audio source`);
      return Promise.resolve();
    }

    return new Promise(async (resolve) => {
      try {
        // Reset if already playing
        if (!audio.paused) {
          console.log(`[StreamTV] Resetting ${type} audio`);
          audio.pause();
          audio.currentTime = 0;
          await new Promise((r) => setTimeout(r, 100));
        }

        // Wait for audio to be ready (readyState 3 = HAVE_FUTURE_DATA)
        if (audio.readyState < 3) {
          console.log(
            `[StreamTV] Waiting for ${type} audio to load (readyState: ${audio.readyState})...`,
          );

          await new Promise((res, rej) => {
            const timeout = setTimeout(() => {
              console.warn(`[StreamTV] ${type} audio load timeout`);
              res(); // Continue anyway
            }, 5000);

            const onReady = () => {
              clearTimeout(timeout);
              cleanup();
              console.log(`[StreamTV] ${type} audio ready`);
              res();
            };

            const onError = (e) => {
              clearTimeout(timeout);
              cleanup();
              console.error(`[StreamTV] ${type} audio error:`, e);
              rej(e);
            };

            const cleanup = () => {
              audio.removeEventListener("canplay", onReady);
              audio.removeEventListener("canplaythrough", onReady);
              audio.removeEventListener("error", onError);
            };

            audio.addEventListener("canplay", onReady, { once: true });
            audio.addEventListener("canplaythrough", onReady, { once: true });
            audio.addEventListener("error", onError, { once: true });

            if (audio.readyState === 0) {
              audio.load();
            }
          });
        }

        audio.volume = 0.7;

        // Small delay for stability
        await new Promise((r) => setTimeout(r, 150));

        console.log(`[StreamTV] Playing ${type} audio...`);
        await audio.play();

        setAudioPlaying((prev) => ({ ...prev, [type]: true }));
        console.log(`[StreamTV] ✓ ${type} audio playing`);
        resolve();
      } catch (err) {
        console.error(
          `[StreamTV] ✗ Failed to play ${type} audio:`,
          err.name,
          err.message,
        );
        setAudioPlaying((prev) => ({ ...prev, [type]: false }));
        resolve(); // Don't reject, just resolve
      }
    });
  };

  const stopAllAudio = async () => {
    const stopPromises = Object.keys(audioRefs).map((type) => {
      return new Promise((resolve) => {
        const audio = audioRefs[type]?.current;
        if (audio && !audio.paused) {
          try {
            audio.pause();
            audio.currentTime = 0;
          } catch (err) {
            console.warn(`Error stopping ${type}:`, err);
          }
        }
        resolve();
      });
    });

    await Promise.all(stopPromises);

    setAudioPlaying({
      pre_hype: false,
      warmup: false,
      workout: false,
      preview: false,
    });

    await new Promise((resolve) => setTimeout(resolve, 100));
  };

  const stopOtherAudio = async (keepType) => {
    const stopPromises = Object.keys(audioRefs).map((type) => {
      return new Promise((resolve) => {
        if (type !== keepType) {
          // NEVER stop workout audio if workout is active (use ref for current value)
          if (type === "workout" && isWorkoutActiveRef.current) {
            resolve();
            return;
          }
          const audio = audioRefs[type]?.current;
          if (audio && !audio.paused) {
            try {
              audio.pause();
              audio.currentTime = 0;
            } catch (err) {
              console.warn(`Error stopping ${type}:`, err);
            }
          }
        }
        resolve();
      });
    });

    await Promise.all(stopPromises);

    setAudioPlaying((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (key !== keepType) {
          // Don't update state for workout audio if workout is active (use ref)
          if (key === "workout" && isWorkoutActiveRef.current) return;
          newState[key] = false;
        }
      });
      return newState;
    });

    await new Promise((resolve) => setTimeout(resolve, 50));
  };

  // Remove cache-busting logic; use media URLs directly

  // Helper function to adjust font size for Jupiter font
  const getAdjustedFontSize = (
    baseSize,
    fontFamily,
    scale = 1.6,
    dependentFont = null,
  ) => {
    if (fontFamily && fontFamily.toLowerCase().includes("jupiter")) {
      // Extract numeric value from size string (e.g., "4rem" -> 4)
      const numericValue = parseFloat(baseSize);
      // Increase by scale for Jupiter font
      const adjustedValue = numericValue * scale;
      // Return with same unit
      return baseSize.replace(
        numericValue.toString(),
        adjustedValue.toFixed(1),
      );
    } else if (
      dependentFont &&
      dependentFont.toLowerCase().includes("jupiter")
    ) {
      // Extract numeric value from size string (e.g., "4rem" -> 4)
      const numericValue = parseFloat(baseSize);
      // Increase by scale for Jupiter font
      const adjustedValue = numericValue * scale;
      // Return with same unit
      return baseSize.replace(
        numericValue.toString(),
        adjustedValue.toFixed(1),
      );
    }
    return baseSize;
  };

  const getAdjustedLineHeight = (baseSize, fontFamily) => {
    if (fontFamily && fontFamily.toLowerCase().includes("jupiter")) {
      // Extract numeric value from size string (e.g., "4rem" -> 4)
      const numericValue = parseFloat(baseSize);
      // Increase by 60% for Jupiter font
      const adjustedValue = numericValue * 1.6;
      // Return with same unit
      return baseSize.replace(
        numericValue.toString(),
        adjustedValue.toFixed(1),
      );
    }
    return null;
  };

  const getAdjustedRotation = (baseRotation, fontFamily) => {
    if (fontFamily && fontFamily.toLowerCase().includes("jupiter")) {
      return `rotate(-5deg)`;
    }
    return baseRotation;
  };

  const processTasksData = (tasks) => {
    if (!tasks) return [];

    const result = [];

    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];

      // Check if this is a separator row (dash or empty in all fields)
      const isSeparator =
        (task.name === "-" || task.name === "") &&
        (task.sets === "-" || task.sets === "" || task.sets === null) &&
        (task.reps === "-" || task.reps === "" || task.reps === null) &&
        (task.tempo === "-" || task.tempo === "" || task.tempo === null);

      if (isSeparator) {
        // Skip the separator row entirely
        continue;
      }

      // Check if the previous row was a separator
      let isPreviousSeparator = false;
      if (i > 0) {
        const previousTask = tasks[i - 1];
        isPreviousSeparator =
          (previousTask.name === "-" || previousTask.name === "") &&
          (previousTask.sets === "-" ||
            previousTask.sets === "" ||
            previousTask.sets === null) &&
          (previousTask.reps === "-" ||
            previousTask.reps === "" ||
            previousTask.reps === null) &&
          (previousTask.tempo === "-" ||
            previousTask.tempo === "" ||
            previousTask.tempo === null);
      }

      // Check if this is a test exercise
      const isTestExercise =
        task.name &&
        (task.name.toLowerCase().includes("test") ||
          task.name.toLowerCase().includes("test exercise"));

      // Add the task - if it's a test exercise, NEVER mark it as section header
      result.push({
        ...task,
        isSectionHeader: isPreviousSeparator && !isTestExercise,
      });
    }

    return result;
  };

  // Helper: get average/dominant color from an image src
  // const getAverageColorFromImage = (src, fallback = "#4a2781") =>
  //   new Promise((resolve) => {
  //     if (!src) return resolve(fallback);
  //     const img = new Image();
  //     img.crossOrigin = "Anonymous";
  //     img.src = src;
  //     img.onload = () => {
  //       try {
  //         const canvas = document.createElement("canvas");
  //         const w = 40,
  //           h = 40;
  //         canvas.width = w;
  //         canvas.height = h;
  //         const ctx = canvas.getContext("2d");
  //         ctx.drawImage(img, 0, 0, w, h);
  //         const data = ctx.getImageData(0, 0, w, h).data;
  //         let r = 0,
  //           g = 0,
  //           b = 0,
  //           count = 0;
  //         for (let i = 0; i < data.length; i += 4) {
  //           const alpha = data[i + 3];
  //           if (alpha < 125) continue;
  //           r += data[i];
  //           g += data[i + 1];
  //           b += data[i + 2];
  //           count++;
  //         }
  //         if (!count) return resolve(fallback);
  //         r = Math.round(r / count);
  //         g = Math.round(g / count);
  //         b = Math.round(b / count);
  //         const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  //         resolve(hex);
  //       } catch (err) {
  //         resolve(fallback);
  //       }
  //     };
  //     img.onerror = () => resolve(fallback);
  //   });

  useEffect(() => {
    const video = document.createElement("video");
    const isNativeHLS = video.canPlayType("application/vnd.apple.mpegurl");
    setHlsSupported(
      isNativeHLS || (typeof Hls !== "undefined" && Hls.isSupported()),
    );
  }, []);

  // update backgroundColor whenever selectedImage changes
  // useEffect(() => {
  //   let mounted = true;
  //   if (selectedImage) {
  //     getAverageColorFromImage(selectedImage, "#4a2781").then((hex) => {
  //       if (mounted) setBackgroundColor(hex);
  //     });
  //   } else {
  //     setBackgroundColor("#4a2781");
  //   }
  //   return () => {
  //     mounted = false;
  //   };
  // }, [selectedImage]);

  const columns = [
    {
      title: (
        <strong style={{ fontSize: "1.2em" }}>
          {workout?.blockName || workout?.title || "EXERCISE"}
        </strong>
      ),
      dataIndex: "name",
      onHeaderCell: () => ({
        style: {
          background: textColor,
          WebkitTextStroke: "1px #000",
        },
      }),
      onCell: (record) => ({
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: {
          color: font2Color,
          textAlign: "center",
          WebkitTextStroke: record.isSectionHeader ? "1px #000" : "",
          borderRadius: "10px 0px 0px 0px",
        },
      }),
      render: (text, record) => <strong>{text}</strong>,
    },
    {
      title: "SETS",
      dataIndex: "sets",
      align: "center",
      onHeaderCell: () => ({
        style: {
          background: textColor,
          WebkitTextStroke: "1px #000",
        },
      }),
      onCell: (record) => ({
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: {
          color: font2Color,
          textAlign: "center",
          WebkitTextStroke: record.isSectionHeader ? "0.5px #000" : "",
        },
      }),
      render: (t, record) => <strong>{t || ""}</strong>,
    },
    {
      title: "REPS",
      dataIndex: "reps",
      align: "center",
      onHeaderCell: () => ({
        style: {
          background: textColor,
          WebkitTextStroke: "1px #000",
        },
      }),
      onCell: (record) => ({
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: {
          color: font2Color,
          textAlign: "center",
          WebkitTextStroke: record.isSectionHeader ? "0.5px #000" : "",
        },
      }),
      render: (t, record) => <strong>{t || ""}</strong>,
    },
    {
      title: "TEMPO",
      dataIndex: "tempo",
      align: "center",
      onHeaderCell: () => ({
        style: {
          background: textColor,
          WebkitTextStroke: "1px #000",
        },
      }),
      onCell: (record) => ({
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: {
          color: font2Color,
          textAlign: "center",
          WebkitTextStroke: record.isSectionHeader ? "0.5px #000" : "",
          borderRadius: "0px 10px 0px 0px",
        },
      }),
      render: (t, record) => <strong>{t || ""}</strong>,
    },
  ];

  // AUTO-SET DAY AND BLOCK FROM URL
  useEffect(() => {
    const urlDay = searchParams.get("day")?.toUpperCase();
    const validDays = [
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
      "SUNDAY",
    ];

    if (urlDay && validDays.includes(urlDay)) {
      setDay(urlDay);
    } else {
      const today = new Date()
        .toLocaleString("en-US", { weekday: "long" })
        .toUpperCase();
      setDay(today);
    }

    const urlBlock = searchParams.get("block")?.toUpperCase();
    if (/^[A-H]_BLOCK$/.test(urlBlock)) {
      setBlock(urlBlock);
      blockRef.current = urlBlock; // Update ref
      console.log(`[URL] Block set to: ${urlBlock}`);
    }
  }, [searchParams]);
  // Helper function to get the token (prioritize URL token)
  const getToken = () => {
    // First try URL token
    if (urlToken) {
      return urlToken;
    }

    // Then try localStorage
    const localStorageToken = localStorage.getItem("accessToken");
    if (localStorageToken) {
      return localStorageToken;
    }

    // Finally try sessionStorage
    return sessionStorage.getItem("urlToken");
  };

  // FETCH WORKOUT - Updated to use URL token
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setBorderColor("#FCDC08");
      setTextColor("#FCDC08");
      try {
        const token = getToken();

        if (!token) {
          message.error("No authentication token found");
          setLoading(false);
          return;
        }

        console.log(`[FETCH] Fetching data for day: ${day}, block: ${block}`);

        const res = await fetch(`${apiPath.baseURL}${apiPath.getWeeklyPlan}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (data.status === 200) {
          const dayData = data.full_week?.[day] || data.today;
          const blockData = dayData?.data?.[block];

          console.log(`[FETCH] Block data for ${block}:`, blockData);

          let newBorderColor = "#FCDC08";
          let newTextColor = "#FCDC08";

          if (
            dayData?.border_color &&
            /^#([0-9A-F]{3}){1,2}$/i.test(dayData.border_color)
          ) {
            newBorderColor = dayData.border_color;
          }
          // Also check theme.border_color if exists
          else if (
            dayData?.theme?.border_color &&
            /^#([0-9A-F]{3}){1,2}$/i.test(dayData.theme.border_color)
          ) {
            newBorderColor = dayData.theme.border_color;
          }

          // Check if current day has text color
          if (
            dayData?.text_color &&
            /^#([0-9A-F]{3}){1,2}$/i.test(dayData.text_color)
          ) {
            newTextColor = dayData.text_color;
          }
          // Also check theme.text_color if exists
          else if (
            dayData?.theme?.text_color &&
            /^#([0-9A-F]{3}){1,2}$/i.test(dayData.theme.text_color)
          ) {
            newTextColor = dayData.theme.text_color;
          }

          if (
            dayData?.background_color &&
            /^#([0-9A-F]{3}){1,2}$/i.test(dayData.background_color)
          ) {
            setBackgroundColor(dayData.background_color);
          } else {
            setBackgroundColor("#4a2781");
          }

          // Apply the colors
          setBorderColor(newBorderColor);
          setTextColor(newTextColor);

          // Load font1 and font2
          if (dayData?.font1) {
            setFont1(dayData.font1);
          } else {
            setFont1("'Horizon', sans-serif");
          }

          if (dayData?.font1_color) {
            setFont1Color(dayData.font1_color);
          } else {
            setFont1Color("#FCDC08");
          }

          if (dayData?.font2) {
            setFont2(dayData.font2);
          } else {
            setFont2("'Horizon', sans-serif");
          }

          if (dayData?.font2_color) {
            setFont2Color(dayData.font2_color);
          } else {
            setFont2Color("#FCDC08");
          }

          // Store media data for audio playback
          const mediaData = dayData?.media || {};
          setMedia(mediaData);

          setWorkout({
            program: dayData?.name || "4 HUNNID",
            title: blockData?.title || block.replace("_", " "),
            blockName: blockData?.blockName || blockData?.title || "",
            duration: blockData?.duration || "18:00",
            tasks: blockData?.tasks || [],
            cooldown: dayData?.cooldown || "",
            timeSegments: blockData?.timeSegments || [],
          });
          const parseDurationToSeconds = (duration) => {
            if (typeof duration === "string" && duration.includes(":")) {
              const [minutes, seconds] = duration.split(":").map(Number);
              return minutes * 60 + seconds;
            } else if (typeof duration === "number") {
              return Math.round(duration * 60);
            }
            return 18 * 60; // Default 18 minutes
          };

          const uploadedBackgroundImage = dayData?.media?.background_image;

          if (uploadedBackgroundImage) {
            setSelectedImage(uploadedBackgroundImage);
          } else {
            const dayImage = dayData?.image;
            if (dayImage && availableImages[dayImage]) {
              setSelectedImage(availableImages[dayImage]);
            } else {
              setSelectedImage(newLogoImage);
            }
          }
        } else {
          message.error(data.message || "Failed to load workout data");
        }
      } catch (err) {
        message.error("Load failed");
        setSelectedImage(newLogoImage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [day, block, urlToken]);

  // Countdown timer function
  const startCountdown = (duration) => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }

    setCountdown(duration);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setCountdownInterval(timer);
  };

  // Segment timer function
  const startSegmentTimer = (segments) => {
    if (segmentInterval) clearInterval(segmentInterval);

    if (isPaused) return;

    let segmentIndex = 0;
    let isWorking = true;

    const hasWorkRest = segments[0]?.workingTime !== undefined;

    let timeLeft;
    if (hasWorkRest) {
      timeLeft = segments[0]?.workingTime;
      setTotalSegments(segments.length * 2);
      setIsWorkingPhase(true);
    } else {
      timeLeft = segments[0]?.duration;
      setTotalSegments(segments.length);
      setIsWorkingPhase(false);
      isWorking = false;
    }

    setCurrentSegment(1);
    setSegmentTime(timeLeft);

    const timer = setInterval(() => {
      setSegmentTime((prev) => {
        if (isPaused) {
          return prev;
        }
        if (prev <= 1) {
          if (hasWorkRest) {
            if (isWorking) {
              const restTime = segments[segmentIndex]?.restTime;
              if (restTime > 0) {
                isWorking = false;
                setIsWorkingPhase(false);
                setSegmentTime(restTime);
                return restTime;
              } else {
                segmentIndex++;
                if (segmentIndex >= segments.length) {
                  clearInterval(timer);
                  return 0;
                }
                setCurrentSegment(segmentIndex + 1);
                const workTime = segments[segmentIndex]?.workingTime;
                setSegmentTime(workTime);
                return workTime;
              }
            } else {
              segmentIndex++;
              if (segmentIndex >= segments.length) {
                clearInterval(timer);
                return 0;
              }
              isWorking = true;
              setIsWorkingPhase(true);
              setCurrentSegment(segmentIndex + 1);
              const workTime = segments[segmentIndex]?.workingTime;
              setSegmentTime(workTime);
              return workTime;
            }
          } else {
            segmentIndex++;
            if (segmentIndex >= segments.length) {
              clearInterval(timer);
              return 0;
            }
            setCurrentSegment(segmentIndex + 1);
            const duration = segments[segmentIndex]?.duration;
            setSegmentTime(duration);
            return duration;
          }
        }
        return prev - 1;
      });
    }, 1000);

    setSegmentInterval(timer);
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
      if (segmentInterval) clearInterval(segmentInterval);
    };
  }, [countdownInterval, segmentInterval]);

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Helper function to check if message is for current block
  const isMessageForCurrentBlock = (data) => {
    // For messages with payload.current_block
    if (data.payload?.current_block) {
      const matches = data.payload.current_block === blockRef.current;
      // if (data.type === "timer_update") {
      //   console.log(
      //     `[TIMER_UPDATE] Message block: ${data.payload.current_block}, Current block (ref): ${blockRef.current}, Matches: ${matches}`,
      //   );
      // }
      return matches;
    }

    // For messages with current_block in payload
    if (data.payload?.block) {
      return data.payload.block === blockRef.current;
    }

    // For repetition countdown messages
    if (
      data.type === "show_repetition_countdown" ||
      data.type === "repetition_countdown_update"
    ) {
      return data.payload?.current_block === blockRef.current;
    }
    // Default: accept messages without specific block targeting
    return true;
  };

  const [themeColor, setThemeColor] = useState("#4a2781");

  // WEBSOCKET – Updated to use URL token
  useEffect(() => {
    const token = getToken();

    if (!token) {
      message.error("Authentication required");
      return;
    }

    console.log(`[WEBSOCKET] Connecting for block: ${block}`);

    // const userId = localStorage.getItem("userId");
    const baseUrl = apiPath.baseURL
      .replace("/api/v1/", "/")
      .replace("http://", "ws://")
      .replace("https://", "wss://");
    const wsUrl = `${baseUrl}ws/preview/?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);

    let reconnectTimeout;
    let isConnected = false;
    let isMounted = true;

    ws.onopen = () => {
      isConnected = true;
    };

    ws.onclose = (e) => {
      isConnected = false;

      // Clear any pending intervals on disconnect
      if (countdownInterval) {
        clearInterval(countdownInterval);
        setCountdownInterval(null);
      }
      if (segmentInterval) {
        clearInterval(segmentInterval);
        setSegmentInterval(null);
      }
    };

    ws.onerror = (err) => {
      console.error(`[WEBSOCKET] Error for day: ${day}, block: ${block}`, err);
    };

    ws.onmessage = async (e) => {
      try {
        const data = JSON.parse(e.data);
        // FILTER BY DAY FIRST

        const incomingColor =
          data.color ||
          data.payload?.color ||
          data.payload?.theme_color ||
          data.payload?.themeColor;
        if (incomingColor && /^#([0-9A-F]{3}){1,2}$/i.test(incomingColor)) {
          try {
            setThemeColor(incomingColor);
            // setBackgroundColor(incomingColor);
            // continue processing other message types (do not early return)
          } catch (err) {}
        }
        const messageDay = data.day?.toUpperCase().split("_")[0];
        if (messageDay !== day.toUpperCase()) {
          return;
        }
        if (data.type === "update_text_color") {
          const incomingTextColor = data.payload?.text_color || data.text_color;
          if (
            incomingTextColor &&
            /^#([0-9A-F]{3}){1,2}$/i.test(incomingTextColor)
          ) {
            setTextColor(incomingTextColor);
          }
          return;
        }

        // Handle border color updates
        if (data.type === "update_border_color") {
          const incomingBorderColor =
            data.payload?.border_color || data.border_color;
          if (
            incomingBorderColor &&
            /^#([0-9A-F]{3}){1,2}$/i.test(incomingBorderColor)
          ) {
            setBorderColor(incomingBorderColor);
          }
          return;
        }

        if (data.type === "update_font_family") {
          const payload = data.payload || {};
          const incomingFont1 = payload.font1 || data.font1;
          const incomingFont2 = payload.font2 || data.font2;
          const incomingFont = payload.font_family || data.font_family;

          // Update font1 if provided
          if (incomingFont1) {
            setFont1(incomingFont1);
          } else if (incomingFont) {
            // Fallback for legacy font_family updates
            setFont1(incomingFont);
          }

          // Update font2 if provided
          if (incomingFont2) {
            setFont2(incomingFont2);
          }
          return;
        }

        if (data.type === "update_font1_color") {
          const incomingFont1Color =
            data.payload?.font1_color || data.font1_color;
          if (
            incomingFont1Color &&
            /^#([0-9A-F]{3}){1,2}$/i.test(incomingFont1Color)
          ) {
            setFont1Color(incomingFont1Color);
          }
          return;
        }

        if (data.type === "update_font2_color") {
          const incomingFont2Color =
            data.payload?.font2_color || data.font2_color;
          if (
            incomingFont2Color &&
            /^#([0-9A-F]{3}){1,2}$/i.test(incomingFont2Color)
          ) {
            setFont2Color(incomingFont2Color);
          }
          return;
        }

        if (data.type === "update_background_color") {
          const incomingBgColor =
            data.payload?.background_color || data.background_color;
          if (
            incomingBgColor &&
            /^#([0-9A-F]{3}){1,2}$/i.test(incomingBgColor)
          ) {
            setBackgroundColor(incomingBgColor);
          }
          return;
        }

        // FILTER BY BLOCK FOR SPECIFIC MESSAGE TYPES
        const shouldFilterByBlock = [
          "show_countdown",
          "countdown_update",
          "timer_update",
          "show_transition",
          "workout_complete",
          "show_table",
          "show_repetition_countdown",
          "repetition_countdown_update",
        ].includes(data.type);

        if (shouldFilterByBlock && !isMessageForCurrentBlock(data)) {
          return;
        }

        // Handle pause/resume for ALL blocks (no filtering)
        if (data.type === "pause_workout" || data.type === "workout_paused") {
          setIsPaused(true);
          // Pause workout audio when workout is paused
          if (audioPlayingRef.current.workout) {
            const audio = audioRefs.workout?.current;
            if (audio && !audio.paused) {
              try {
                audio.pause();
                setAudioPlaying((prev) => ({ ...prev, workout: false }));
              } catch (err) {
                console.error("[Audio] Error pausing workout audio:", err);
              }
            }
          }

          if (segmentInterval) {
            clearInterval(segmentInterval);
            setSegmentInterval(null);
          }
          if (countdownInterval) {
            clearInterval(countdownInterval);
            setCountdownInterval(null);
          }
          return;
        }

        if (data.type === "resume_workout" || data.type === "workout_resumed") {
          setIsPaused(false);
          // Resume workout audio when workout is resumed
          if (media.workout_audio && isWorkoutActiveRef.current) {
            const audio = audioRefs.workout?.current;
            if (audio && audio.paused && !audioPlayingRef.current.workout) {
              audio
                .play()
                .catch((err) =>
                  console.error("[Audio] Error resuming workout audio:", err),
                );
              setAudioPlaying((prev) => ({ ...prev, workout: true }));
            }
          }
          return;
        }

        const videoUrl = data.video_url || data.payload?.video_url;
        let finalVideoUrl = videoUrl;
        let isHLS = false;

        if (videoUrl) {
          // Check if it's already HLS
          if (videoUrl.includes(".m3u8")) {
            isHLS = true;
            finalVideoUrl = videoUrl;
          } else {
            // Try to get HLS version
            try {
              const token = getToken();
              const mediaType =
                data.type === "show_pre_hype"
                  ? "pre_hype_video"
                  : "warmup_video";

              const response = await fetch(
                `${apiPath.baseURL}/api/get-media-stream/${day}/${mediaType}/`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                },
              );

              if (response.ok) {
                const streamData = await response.json();
                if (streamData.data.media_type === "hls") {
                  isHLS = true;
                  finalVideoUrl = streamData.data.stream_url;
                }
              }
            } catch (err) {}
          }
        }
        // Handle different message types
        switch (data.type) {
          case "show_pre_hype":
          case "show_warmup":
          case "show_preview":
            if (finalVideoUrl) {
              const modeType =
                data.type === "show_pre_hype"
                  ? "pre_hype"
                  : data.type === "show_warmup"
                    ? "warmup"
                    : "preview";

              setMode(modeType);
              setCurrentVideo(finalVideoUrl);

              // Stop other audio before playing this mode's audio
              await stopOtherAudio(modeType);

              // Play audio for this mode if available
              if (media[`${modeType}_audio`]) {
                setTimeout(() => playAudio(modeType), 150);
              }

              // Check if it's HLS
              if (finalVideoUrl.includes(".m3u8")) {
                setVideoManifest({
                  url: finalVideoUrl,
                  isHLS: true,
                  type: modeType,
                });
              } else {
                // For direct MP4 files
                setVideoManifest({
                  url: finalVideoUrl,
                  isHLS: false,
                  type: modeType,
                });
              }
            } else if (data.type === "show_preview") {
              // No preview video available, show table instead
              setMode("table");
              setCountdown(0);
            }
            break;

          case "show_table":
            // During workout, show_table should not change mode or affect audio
            if (!isWorkoutActiveRef.current) {
              setMode("table");
            }
            setCountdown(0);

            // Never stop audio during active workout
            // Audio control is commented out to prevent interference
            break;

          case "show_hero":
            setMode("hero");
            setWorkoutComplete(false);

            // Stop all audio when returning to hero mode
            stopAllAudio();
            break;

          case "workout_stopped":
            setMode("hero");
            setIsPaused(false);
            setIsWorkoutActive(false); // Mark workout as no longer active
            setWorkoutComplete(false);

            // Stop all audio
            stopAllAudio();
            break;

          case "show_countdown":
            setMode("countdown");
            setCountdown(data.payload.duration);
            setTransitionMessage(data.payload.message);

            // Show table when countdown reaches 10 seconds
            if (data.payload.duration <= 10) {
              setMode("table");
            }
            break;

          case "countdown_update":
            setCountdown(data.payload.remaining_time);

            // Show table when 10 seconds or less remain
            if (
              data.payload.remaining_time <= 10 &&
              modeRef.current !== "table"
            ) {
              setMode("table");
            }
            break;

          case "timer_update":
            const wasWorkoutMode = modeRef.current === "workout";
            setMode("workout");
            setIsWorkoutActive(true); // Mark workout as active
            setWorkoutTime(data.payload.remaining_time);
            setTotalWorkoutTime(data.payload.total_time);
            setCurrentBlock(data.payload.current_block);

            if (currentVideo) {
              setCurrentVideo("");
              setVideoManifest(null);
            }

            if (segmentInterval) {
              clearInterval(segmentInterval);
              setSegmentInterval(null);
            }

            // Start workout audio when workout first begins or if it's not playing (use ref)
            if (media.workout_audio && !audioPlayingRef.current.workout) {
              stopOtherAudio("workout");
              setTimeout(() => playAudio("workout"), 100);
            }

            if (data.payload.segments && data.payload.segments.length > 0) {
              const segments = data.payload.segments;
              const totalSegs = data.payload.total_segments || segments.length;
              const currentSegIndex = (data.payload.current_segment || 1) - 1;

              setTotalSegments(totalSegs);
              setCurrentSegment(currentSegIndex + 1);

              let segTime =
                typeof data.payload.segment_time === "number"
                  ? data.payload.segment_time
                  : null;

              if (segTime === null) {
                const seg = segments[currentSegIndex] || segments[0];
                segTime =
                  seg?.segment_time ??
                  seg?.workingTime ??
                  seg?.duration ??
                  seg?.restTime ??
                  0;
              }

              if (
                typeof segTime === "number" &&
                segTime >= 0 &&
                segTime < (data.payload.total_time || Infinity)
              ) {
                setSegmentTime(segTime);
              } else {
                setSegmentTime(0);
              }

              setIsWorkingPhase(
                data.payload.is_working !== undefined
                  ? data.payload.is_working
                  : true,
              );
            } else {
              setTotalSegments(0);
              setCurrentSegment(0);
              setSegmentTime(0);
              setIsWorkingPhase(true);
            }
            break;

          case "show_transition":
            setMode("transition");
            if (typeof data.payload.workout_transition_message === "string") {
              setWorkoutTransitionMessage(data.payload.workout_transition_message);
            }
            setCountdown(data.payload.duration);
            setTransitionMessage(data.payload.message);
            startCountdown(data.payload.duration);
            break;

          case "workout_complete":
            if (isMessageForCurrentBlock(data)) {
              setMode("hero");
              setIsPaused(false);
              setIsWorkoutActive(false); // Mark workout as no longer active
              setWorkoutComplete(true);

              // Stop all audio when workout completes
              stopAllAudio();

              // ALWAYS use total_all_blocks_time when available
              // This is the TOTAL time of ALL blocks (e.g., 40 seconds for A+B)
              const customMessage =
                data.payload?.completion_message || "You've just completed!";
              setCompletionMessage(customMessage);

              const customTopMessage =
                data.payload?.completion_top_message || "Great Job!";
              setCompletionTopMessage(customTopMessage);

              const customTransitionMessage =
                data.payload?.workout_transition_message || "Change Blocks Now!";
              setWorkoutTransitionMessage(customTransitionMessage);

              let totalTimeToShow =
                data.payload?.total_all_blocks_time ||
                data.payload?.total_time ||
                0;

              setTotalWorkoutTime(totalTimeToShow);

              // Get other info
              const totalReps = data.payload?.total_repetitions_completed || 1;
              const totalBlocks = data.payload?.total_blocks_completed || 1;

              setTotalRepetitions(totalReps);

              // Show appropriate message
              if (data.payload?.is_final_block) {
                // message.success(`All ${totalBlocks} blocks completed! (${totalReps} reps)`);
              } else if (data.payload?.is_block_complete) {
                // message.success(`Block ${data.payload.completed_block} completed!`);
              } else {
                // message.success(`Workout Complete! (${totalReps} repetitions)`);
              }
            }
            break;

          case "show_repetition_countdown":
            setMode("repetition_countdown");
            if (typeof data.payload.workout_transition_message === "string") {
              setWorkoutTransitionMessage(data.payload.workout_transition_message);
            }
            setCountdown(data.payload.duration);
            setRepetitionMessage(data.payload.message);
            setCurrentRepetition(data.payload.current_repetition || 1);
            setTotalRepetitions(data.payload.total_repetitions || 3);
            break;

          case "repetition_countdown_update":
            setCountdown(data.payload.remaining_time);
            setCurrentRepetition(data.payload.current_repetition || 1);
            setTotalRepetitions(data.payload.total_repetitions || 3);
            break;

          default:
        }
      } catch (err) {}
    };

    ws.onerror = (error) => {};

    ws.onclose = (event) => {
      isConnected = false;

      // Clear any existing timeout
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      // Only attempt reconnection if component is still mounted and not a normal closure
      if (isMounted && event.code !== 1000) {
        reconnectTimeout = setTimeout(() => {
          if (isMounted) {
            console.log("[WEBSOCKET] Reconnecting...");
            // Note: Reconnection requires re-running the effect
          }
        }, 3000);
      }
    };

    // Cleanup function
    return () => {
      isMounted = false;
      isConnected = false;

      // Clear any pending timers
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }

      // Clear workout intervals
      if (countdownInterval) {
        clearInterval(countdownInterval);
        setCountdownInterval(null);
      }
      if (segmentInterval) {
        clearInterval(segmentInterval);
        setSegmentInterval(null);
      }

      // Stop all audio on unmount
      Object.keys(audioRefs).forEach((type) => {
        const audio = audioRefs[type]?.current;
        if (audio && !audio.paused) {
          try {
            audio.pause();
            audio.currentTime = 0;
          } catch (err) {
            console.warn(`Error stopping ${type} audio on cleanup:`, err);
          }
        }
      });

      // Close WebSocket with normal closure code
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close(1000, "Component unmounting");
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, block, urlToken]); // Added urlToken dependency

  if (loading) {
    return (
      <div
        style={{
          fontFamily: font2,
          height: "100vh",
          background: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" tip="Loading Stream..." />
      </div>
    );
  }

  const getHLSStreamUrl = async (day, category) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${apiPath.baseURL}/api/get-media-stream/${day}/${category}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        return data.data.stream_url;
      }
    } catch (err) {}
    return null;
  };

  // VIDEO PLAYER
  if (mode === "pre_hype" || mode === "warmup" || mode === "preview") {
    return (
      <div
        style={{
          fontFamily: font2,
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "#000",
          overflow: "hidden",
          zIndex: 9999,
        }}
      >
        {/* Add a container div for better control */}
        <div
          className="video-container"
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
          }}
        >
          {videoManifest?.isHLS ? (
            <HLSVideoPlayer
              src={currentVideo}
              autoPlay={true}
              loop={true}
              muted={true}
              controls={false}
              onReady={() => {
                // The HLSVideoPlayer component should handle autoplay
              }}
              onError={(error) => {
                // Fallback to direct MP4 if available
                if (currentVideo.includes(".m3u8")) {
                  const mp4Url = currentVideo.replace(".m3u8", ".mp4");
                  setCurrentVideo(mp4Url);
                  setVideoManifest((prev) => ({ ...prev, isHLS: false }));
                }
              }}
            />
          ) : (
            <video
              key={currentVideo}
              src={currentVideo}
              autoPlay
              loop
              muted={true}
              playsInline
              preload="auto"
              crossorigin="anonymous"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                message.error("Video failed to load");
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // TRANSITION MODE
  if (mode === "transition") {
    return (
      <div
        style={{
          fontFamily: font2,
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: backgroundColor || "#16213e",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            fontSize: getAdjustedFontSize("4rem", font2),
            fontWeight: "bold",
            color: font2Color,
            marginBottom: "2rem",
            transform: getAdjustedRotation(0, font2),
            display: "inline-block",
          }}
        >
          {formatTime(countdown)}
        </div>

        <div
          style={{
            fontSize: getAdjustedFontSize("2.5rem", font2),
            color: font2Color,
            textAlign: "center",
            transform: getAdjustedRotation(0, font2),
            display: "inline-block",
          }}
        >
          {transitionMessage}
        </div>
        <div
          style={{
            fontSize: getAdjustedFontSize("1.5rem", font2),
            color: font2Color,
            marginTop: "2rem",
            textAlign: "center",
            transform: getAdjustedRotation(0, font2),
            display: "inline-block",
          }}
        >
          Next: {currentBlock.replace("_", " ")}
        </div>
      </div>
    );
  }

  // WORKOUT MODE (Timer + Table)
  if (mode === "workout") {
    return (
      <div
        className="background_stream background_stream-for-vhh"
        style={{
          fontFamily: font2,
          "--stream-font": font2,
          "--stream-font-1": font1,
          "--stream-font-1-color": font1Color,
          "--stream-font-2-color": font2Color,
        }}
      >
        <div className="content__stream">
          <div
            className="stream for-vhh-mainjhj position-inside"
            style={{
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* <div className="position-adjust"> */}
            <div style={{ flex: 4 }} className="for-styling-fonss">
              <div
                style={{
                  margin: "0 10px 4px 20px",
                  height: "100%",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                }}
                className="for-margin-remove new-remove-amaijk"
              >
                <h1
                  style={{
                    color: font1Color,
                    fontSize: getAdjustedFontSize("4rem", font1),
                    transform: getAdjustedRotation(0, font1),
                    display: "inline-block",
                    fontWeight: "500",
                    margin: 0,
                    // Use font1 for block title (header)
                    fontFamily: font1,
                    WebkitTextStroke: "2px #000",
                  }}
                  className="for-res-compleatedd"
                >
                  {block.replace("_", " ")}
                </h1>
                {totalSegments > 0 ? (
                  <>
                    <div
                      className="time-on-offe-buttons"
                      style={{
                        fontSize: getAdjustedFontSize("5rem", font2),
                        fontFamily: font2,
                        fontWeight: "bold",
                        color: isPaused ? "#FF5722" : font2Color,
                        WebkitTextStroke: "2px #000",
                        transform: getAdjustedRotation(0, font2),
                        display: "inline-block",
                      }}
                    >
                      <span> {formatTime(segmentTime)} </span>
                      {isPaused ? "OFF" : isWorkingPhase ? "ON" : "OFF"}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      fontSize: getAdjustedFontSize("5rem", font2),
                      fontWeight: "bold",
                      color: isPaused ? "#FF5722" : "#fff",
                      transform: getAdjustedRotation(0, font2),
                      display: "inline-block",
                    }}
                    className="new-mainjhjhjhjhjh"
                  >
                    {formatTime(workoutTime)} {isPaused && "OFF"}
                  </div>
                )}
              </div>
              <div
                className="workout-table-wrapper"
                style={{ fontFamily: font2, color: font2Color }}
              >
                <Table
                  columns={columns}
                  dataSource={processTasksData(workout?.tasks).map((t, i) => ({
                    key: i,
                    ...t,
                  }))}
                  pagination={false}
                  bordered={false}
                  className="new-table-main"
                  components={{
                    body: {
                      row: (props) => {
                        const { children, ...restProps } = props;
                        const dataRowKey = restProps["data-row-key"];
                        const record =
                          dataRowKey != null
                            ? processTasksData(workout?.tasks)[dataRowKey]
                            : null;

                        // Add a spacer row before section headers
                        if (record?.isSectionHeader) {
                          return (
                            <>
                              <tr
                                key={`spacer-${restProps["data-row-key"]}`}
                                style={{
                                  height: "20px",
                                  background: "transparent",
                                }}
                              >
                                <td
                                  className="streamlit-section-header"
                                  colSpan={4}
                                  style={{ padding: "0", height: "20px" }}
                                >
                                  &nbsp;
                                </td>
                              </tr>
                              <tr
                                {...restProps}
                                key={`header-${restProps["data-row-key"]}`}
                                style={{
                                  background: textColor,
                                  fontWeight: "bold",
                                }}
                              >
                                {children}
                              </tr>
                            </>
                          );
                        }

                        return <tr {...restProps}>{children}</tr>;
                      },
                    },
                  }}
                  // rowClassName={(record) => record.isSpecial ? "special-exercise-row" : ""}
                  onRow={(record) => ({
                    style: {
                      backgroundColor: record.isSectionHeader ? textColor : "",
                    },
                  })}
                />
                {totalSegments > 0 ? (
                  <div style={{ width: "100%", textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: getAdjustedFontSize("3.5rem", font2),
                        color: "white",
                        fontWeight: "bold",
                        textAlign: "end",
                        WebkitTextStroke: "2px #000",
                        transform: getAdjustedRotation(0, font2),
                        display: "inline-block",
                      }}
                      className="main-new-segmanet-news"
                    >
                      {currentSegment}/{Math.ceil(totalSegments)}
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              {workout?.cooldown && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: "#FCDC08",
                    fontWeight: "bold",
                  }}
                >
                  COOLDOWN: {workout.cooldown}
                </div>
              )}
            </div>
            {/* </div> */}
          </div>
        </div>
        <div className="grunge-layer"></div>
        <div
          className="color-layer"
          style={{ backgroundColor: backgroundColor }}
        ></div>
        <div className="opacity-layer"></div>
        <div
          className="border-layer"
          style={{ borderColor: borderColor }}
        ></div>
      </div>
    );
  }

  if (mode === "repetition_countdown") {
    return (
      <div
        className="background_stream"
        style={{
          fontFamily: font2,
          "--stream-font": font2,
          "--stream-font-1": font1,
          "--stream-font-1-color": font1Color,
          "--stream-font-2-color": font2Color,
        }}
      >
        <div className="content__stream">
          <div
            className="stream"
            style={{
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Left side - CD Image */}
            {countdown <= 0 && (
              <div
                style={{ marginLeft: "-505px" }}
                className="for-margin-remove"
              >
                <div className="cd-container">
                  <img
                    src={selectedImage}
                    style={{
                      width: "300px",
                      height: "300px",
                      objectFit: "cover",
                      borderRadius: "20px",
                    }}
                    alt="Workout Background"
                  />
                </div>
              </div>
            )}
            {/* Right side - Table */}
            <div
              style={{ flex: 4, position: "relative" }}
              className="for-styling-fonss"
            >
              <div
                style={{
                  margin: "0 10px 4px 20px",
                  height: "100%",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                }}
                className="for-margin-remove new-remove-amaijk"
              >
                <h1
                  style={{
                    color: font1Color,
                    fontSize: getAdjustedFontSize("4.8rem", font1),
                    fontWeight: "500",
                    margin: 0,
                    fontFamily: font1,
                    WebkitTextStroke: "2px #000",
                    transform: getAdjustedRotation(0, font1),
                    display: "inline-block",
                  }}
                  className="for-res-compleatedd"
                >
                  {block.replace("_", " ")}
                </h1>

                <WorkoutTimer
                  countdown={countdown}
                  themeColor={themeColor}
                  fontFamily={font2}
                  fontColor={font2Color}
                  getAdjustedFontSize={getAdjustedFontSize}
                  getAdjustedRotation={getAdjustedRotation}
                  workoutTransitionMessage={workoutTransitionMessage}
                />
              </div>

              <div
                className="workout-table-wrapper"
                style={{ fontFamily: font2, color: font2Color }}
              >
                <Table
                  columns={columns}
                  dataSource={processTasksData(workout?.tasks).map((t, i) => ({
                    key: i,
                    ...t,
                  }))}
                  pagination={false}
                  bordered={false}
                  className="new-table-main"
                  components={{
                    body: {
                      row: (props) => {
                        const { children, ...restProps } = props;
                        const dataRowKey = restProps["data-row-key"];
                        const record =
                          dataRowKey != null
                            ? processTasksData(workout?.tasks)[dataRowKey]
                            : null;

                        // Add a spacer row before section headers
                        if (record?.isSectionHeader) {
                          return (
                            <>
                              <tr
                                key={`spacer-${dataRowKey}`}
                                style={{
                                  height: "20px",
                                  background: "transparent",
                                }}
                              >
                                <td
                                  className="streamlit-section-header"
                                  colSpan={4}
                                  style={{ padding: "0", height: "20px" }}
                                >
                                  &nbsp;
                                </td>
                              </tr>
                              <tr
                                {...restProps}
                                key={`header-${dataRowKey}`}
                                style={{
                                  background: textColor,
                                  fontWeight: "bold",
                                }}
                              >
                                {children}
                              </tr>
                            </>
                          );
                        }

                        return <tr {...restProps}>{children}</tr>;
                      },
                    },
                  }}
                  // rowClassName={(record) => record.isSpecial ? "special-exercise-row" : ""}
                  onRow={(record) => ({
                    style: {
                      backgroundColor: record.isSectionHeader ? textColor : "",
                    },
                  })}
                />
              </div>

              {workout?.cooldown && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: "#FCDC08",
                    fontWeight: "bold",
                  }}
                >
                  COOLDOWN: {workout.cooldown}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="grunge-layer"></div>
        <div
          className="color-layer"
          style={{ backgroundColor: backgroundColor }}
        ></div>
        <div className="opacity-layer"></div>
        <div
          className="border-layer"
          style={{ borderColor: borderColor }}
        ></div>
      </div>
    );
  }

  // TABLE MODE
  if (mode === "table") {
    return (
      <div
        className="background_stream background_stream-for-vhh"
        style={{
          fontFamily: font2,
          "--stream-font": font2,
          "--stream-font-1": font1,
          "--stream-font-1-color": font1Color,
          "--stream-font-2-color": font2Color,
        }}
      >
        <div className="content__stream">
          <div
            className="stream for-vhh-mainjhj position-inside"
            style={{
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{ flex: 4, position: "relative" }}
              className="for-styling-fonss"
            >
              <div
                style={{
                  margin: "0 10px 4px 20px",
                  height: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
                className="for-margin-remove new-remove-amaijk"
              >
                <h1
                  style={{
                    color: font1Color,
                    fontSize: getAdjustedFontSize("3rem", font1, 1.8),
                    fontWeight: "500",
                    margin: 0,
                    fontFamily: font1,
                    WebkitTextStroke: "2px #000",
                    transform: getAdjustedRotation(0, font1),
                    display: "inline-block",
                  }}
                  className="for-res-compleatedd"
                >
                  {block.replace("_", " ")}
                </h1>
                {countdown > 0 && (
                  <div
                    style={{
                      fontSize: getAdjustedFontSize("3rem", font2),
                      color: font2Color,
                      zIndex: 10,
                      fontWeight: "bold",
                      fontFamily: font2,
                      WebkitTextStroke: "2px #000",
                      transform: getAdjustedRotation(0, font2),
                      display: "inline-block",
                    }}
                    className="for-res-compleatedd for-res-compleatedd1021021202"
                  >
                    STARTS IN {countdown} SECS
                  </div>
                )}
              </div>
              <div
                className="workout-table-wrapper"
                style={{ fontFamily: font2, color: font2Color }}
              >
                <Table
                  columns={columns}
                  dataSource={processTasksData(workout?.tasks).map((t, i) => ({
                    key: i,
                    ...t,
                  }))}
                  pagination={false}
                  bordered={false}
                  className="new-table-main"
                  // rowClassName={(record) => record.isSpecial ? "special-exercise-row" : ""}
                  components={{
                    body: {
                      row: (props) => {
                        const { children, ...restProps } = props;
                        const dataRowKey = restProps["data-row-key"];
                        const record =
                          dataRowKey != null
                            ? processTasksData(workout?.tasks)[dataRowKey]
                            : null;

                        // Add a spacer row before section headers
                        if (record?.isSectionHeader) {
                          return (
                            <>
                              <tr
                                key={`spacer-${dataRowKey}`}
                                style={{
                                  height: "20px",
                                  background: "transparent",
                                }}
                              >
                                <td
                                  className="streamlit-section-header"
                                  colSpan={4}
                                  style={{ padding: "0", height: "20px" }}
                                >
                                  &nbsp;
                                </td>
                              </tr>
                              <tr
                                {...restProps}
                                key={`header-${dataRowKey}`}
                                style={{
                                  background: textColor,
                                  fontWeight: "bold",
                                }}
                              >
                                {children}
                              </tr>
                            </>
                          );
                        }

                        return <tr {...restProps}>{children}</tr>;
                      },
                    },
                  }}
                  onRow={(record) => ({
                    style: {
                      backgroundColor: record.isSectionHeader ? textColor : "",
                    },
                  })}
                />
              </div>
              {workout?.cooldown && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: "#FCDC08",
                    fontWeight: "bold",
                    opacity: 0.9,
                  }}
                >
                  COOLDOWN: {workout.cooldown}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="grunge-layer"></div>
        <div
          className="color-layer"
          style={{ backgroundColor: backgroundColor }}
        ></div>
        <div className="opacity-layer"></div>
        <div
          className="border-layer"
          style={{ borderColor: borderColor }}
        ></div>
      </div>
    );
  }

  // HERO MODE (default)
  return (
    <div
      className="background_stream background_stream-for-vhh"
      style={{
        fontFamily: font2,
        "--stream-font": font2,
        "--stream-font-1": font1,
        "--stream-font-1-color": font1Color,
        "--stream-font-2-color": font2Color,
      }}
    >
      <div className="content__stream">
        <div
          className="stream for-vhh-mainjhj"
          style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className={`for-margin-remove for-margin-remove_newwkjkjkj ${workoutComplete ? "workout-complete_mainn_makjk" : ""}`}
            style={{ marginLeft: "-40px" }}
          >
            <div
              className={`cd-container cd-container_news ${workoutComplete ? "workout-complete_mainn" : ""}`}
            >
              {workoutComplete && (
                <div
                  style={{
                    fontSize: getAdjustedFontSize("3rem", font1),
                    color: font1Color,
                    textAlign: "center",
                    fontWeight: "600",
                    marginBottom: "1rem",
                    fontFamily: font1,
                    fontStyle: "italic",
                    textTransform: "uppercase",
                    WebkitTextStroke: "2px #000",
                    transform: getAdjustedRotation(0, font1),
                    display: "inline-block",
                  }}
                  className="for-res-compleatedd for-res-compleatedd-for-end-screen"
                >
                  {completionTopMessage}
                </div>
              )}

              <img
                src={selectedImage}
                style={{
                  width: "500px",
                  height: "500px",
                  objectFit: "cover",
                  borderRadius: "20px",
                }}
                alt="Workout Background"
              />

              {workoutComplete && (
                <div
                  style={{
                    fontSize: getAdjustedFontSize("2rem", font2),
                    color: font2Color,
                    textAlign: "center",
                    fontWeight: "500",
                    marginTop: "1rem",
                    fontFamily: font2,
                    WebkitTextStroke: "2px #000",
                    transform: getAdjustedRotation(0, font2),
                    display: "inline-block",
                  }}
                  className="for-res-compleatedd for-res-compleatedd-for-end-screen"
                >
                  {completionMessage}
                  {/* {(() => {
                    // If we have a total time from WebSocket, use it
                    if (totalWorkoutTime > 0) {
                      return `Stronger than you were ${formatTime(totalWorkoutTime)} ago!`;
                    } 
                    // Fallback to workout duration from API
                    else if (workout?.duration) {
                      return `Stronger than you were ${workout.duration} ago!`;
                    } 
                    // Default fallback
                    else {
                      return `Stronger than you were 18:00 ago!`;
                    }
                  })()} */}
                </div>
              )}
            </div>
          </div>
          <div className="center-section">
            {/* WORKOUT COMPLETE MESSAGE */}

            {!workoutComplete && (
              // <img src={beginImage} alt="Begins" width="584" />
              <h1
                style={{
                  color: font1Color,
                  fontWeight: "500",
                  textAlign: "center",
                  margin: 0,
                  fontFamily: font1,
                  fontSize: getAdjustedFontSize("6rem", font1, "1.6"),
                  lineHeight: getAdjustedLineHeight("4.5rem", font1),
                  WebkitTextStroke: "2px #000",
                  transform: getAdjustedRotation('none', font1),
                  display: "inline-block",
                }}
                className="for-res-compleatedd"
              >
                WORKOUT <br /> BEGINS
              </h1>
            )}

            {/* REPETITION COUNTDOWN CONTENT */}
            {mode === "repetition_countdown" && (
              <>
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: `linear-gradient(90deg, rgb(52 179 179 / 0%) 40%, ${themeColor} 55%)`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontFamily: font2,
                    zIndex: 9999,
                    opacity: 0.9,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{ transform: "translateX(87%)" }}
                    className="count-down_mainjhj45"
                  >
                    <div
                      style={{
                        fontSize: getAdjustedFontSize("6rem", font2),
                        fontWeight: "400",
                        color: "#ffffff",
                        zIndex: 3,
                        animation: "pulse 1s ease-in-out infinite",
                        transform: getAdjustedRotation(0, font2),
                        display: "inline-block",
                      }}
                    >
                      {formatTime(countdown)} SECS
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* REGULAR HERO/COUNTDOWN CONTENT */}
            {(mode === "hero" || mode === "countdown") && !workoutComplete && (
              <div
                className="stream-title"
                style={{
                  fontFamily: font2,
                  fontSize:
                    countdown > 0 || (mode === "countdown" && countdown === 0)
                      ? getAdjustedFontSize("5rem", font2, 0.6, font1)
                      : getAdjustedFontSize("5rem", font2, 0.6, font1),
                  color:
                    countdown === 0 && mode === "countdown"
                      ? font2Color
                      : countdown > 0
                        ? font2Color
                        : font2Color,
                  animation:
                    countdown > 0 || (mode === "countdown" && countdown === 0)
                      ? "pulse 0.5s ease-in-out"
                      : "none",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  WebkitTextStroke: "2px #000",
                  lineHeight:
                    countdown > 0 || (mode === "countdown" && countdown === 0)
                      ? "120px"
                      : "90px",
                  transform: getAdjustedRotation(0, font2),
                  display: "inline-block",
                }}
              >
                {mode === "countdown"
                  ? countdown === 0
                    ? "GO!"
                    : `${countdown} SECS`
                  : "SOON"}
              </div>
            )}
            {!workoutComplete && (
              <div className="stream-subtitle-wrapper">
                <div className="stream-subtitle" style={{
                  fontFamily: font2,  
                }}>{workout?.program}</div>
                <div
                  className="divider"
                  style={{
                    width: "0.5px",
                    height: "80px",
                    backgroundColor: "#FCDC08",
                  }}
                ></div>
                <div className="stream-subtitle" style={{
                  fontFamily: font2,  
                }}>{workout?.title}</div>
                <div
                  className="divider"
                  style={{
                    width: "0.5px",
                    height: "80px",
                    backgroundColor: "#FCDC08",
                  }}
                ></div>
                <div className="stream-subtitle" style={{
                  fontFamily: font2,  
                }}>
                  {typeof workout?.duration === "string" &&
                  workout.duration.includes(":")
                    ? workout.duration
                    : `${workout?.duration || 18} MINS`}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="grunge-layer"></div>
      <div
        className="color-layer"
        style={{ backgroundColor: backgroundColor }}
      ></div>
      <div className="opacity-layer"></div>
      <div className="border-layer" style={{ borderColor: borderColor }}></div>

      {/* Audio elements for TV panel playback */}
      {media.pre_hype_audio && (
        <audio
          ref={audioRefs.pre_hype}
          src={media.pre_hype_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, pre_hype: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, pre_hype: false }))
          }
          onPlay={() =>
            setAudioPlaying((prev) => ({ ...prev, pre_hype: true }))
          }
        />
      )}
      {media.warmup_audio && (
        <audio
          ref={audioRefs.warmup}
          src={media.warmup_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, warmup: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, warmup: false }))
          }
          onPlay={() => setAudioPlaying((prev) => ({ ...prev, warmup: true }))}
        />
      )}
      {media.workout_audio && (
        <audio
          ref={audioRefs.workout}
          src={media.workout_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, workout: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, workout: false }))
          }
          onPlay={() => setAudioPlaying((prev) => ({ ...prev, workout: true }))}
        />
      )}
      {media.preview_audio && (
        <audio
          ref={audioRefs.preview}
          src={media.preview_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, preview: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, preview: false }))
          }
          onPlay={() => setAudioPlaying((prev) => ({ ...prev, preview: true }))}
        />
      )}
    </div>
  );
};

export default StreamSection;
